import React, { useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import { Close } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { Button } from "../shared/Button/Button";
import {
  AddOnModalTitle,
  AddOnModalButtons,
  AddOnModalWrapper,
  FetalAntigenFieldsWrapper,
} from "./Grid.styles";
import { AddOnMode, AddOnModalData } from "./types";
import { useRequisitionSetter } from "../../recoil/useRequisitionSetter";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import {
  SubmitAddOns,
  RefreshRequisitionRows,
  GetSpecificRequisition,
} from "./Services/GridService";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { WarningIcon } from "../../assets/iconComponents/Warning";
import { themePalette } from "../../theme";
import {
  AddOnLabelName,
  AddOnLabelText,
  AddOnOptions,
  CenteredModal,
  CloseButton,
  ConfirmCheckboxLabel,
  ConfirmMessage,
  Disclaimer,
  ModalBody,
  ModalDivider,
  ModalLoadingBody,
  ModalLoadingOption,
  ModalLoadingPatientColumn,
  ModalLoadingPatientText,
  ModalLoadingText,
  PatientBox,
  PatientColumn,
  PatientLabel,
  PatientValue,
  AddOnModalNotice,
} from "./AddOnModal.styles";
import {
  PLATELET_ANTIGENS,
  RBC_ANTIGENS,
  getSelectedFetalAntigenPanel,
  hasSuccessfulFetalAntigenUpload,
  isFetalAntigenSelectionValid,
  type FetalAntigenFieldSelection,
} from "../NewRequisition/fetalAntigenConstants";
import { FetalAntigenFields } from "../NewRequisition/FetalAntigenFields";
import type { FileUploadInfo } from "../NewRequisition/NewReqFileUploader";
import { OptionLabel } from "../NewRequisition/NewRequisition.styles";

export { hasSuccessfulFetalAntigenUpload } from "../NewRequisition/fetalAntigenConstants";

const ADD_ON_LABELS: Record<AddOnMode, string> = {
  fetal_sex: "Fetal Sex",
  fetal_rhd: "Fetal RhD",
  fetal_antigen: "Fetal Antigen",
};

const NON_ANTIGEN_ADD_ON_MODES: AddOnMode[] = ["fetal_sex", "fetal_rhd"];

interface FetalAntigenSelection {
  antigens: string[];
  isRbc: boolean;
  isPlatelet: boolean;
  hasRhdIncompatibleAntigen: boolean;
  fileUrls: FileUploadInfo;
}

const ANTIGENS = [...RBC_ANTIGENS, ...PLATELET_ANTIGENS];

const AddOnFetalAntigenFields: React.FC<{
  disabled: boolean;
  rhdIncluded: boolean;
  onChange: (selection: FetalAntigenSelection) => void;
}> = ({ disabled, rhdIncluded, onChange }) => {
  const { deviceType } = useDeviceHook();
  const [isRbc, setIsRbc] = useState(false);
  const [isPlatelet, setIsPlatelet] = useState(false);
  const [selected, setSelected] = useState<FetalAntigenFieldSelection>({});
  const [fileUrls, setFileUrls] = useState<FileUploadInfo>({});
  const [isFileUploadProcessing, setFileUploadProcessing] = useState(false);

  useEffect(() => {
    onChange({
      antigens: getSelectedFetalAntigenPanel(selected),
      isRbc,
      isPlatelet,
      hasRhdIncompatibleAntigen: ANTIGENS.some(
        (antigen) => antigen.disabledWhenRhd && selected[antigen.name],
      ),
      fileUrls,
    });
  }, [selected, isRbc, isPlatelet, fileUrls]);

  return (
    <FetalAntigenFieldsWrapper>
      <FetalAntigenFields
        value={{ isRbc, isPlatelet, selected }}
        disabled={disabled}
        rhdIncluded={rhdIncluded}
        expandedAntigensEnabled
        onChange={(selection) => {
          setIsRbc(selection.isRbc);
          setIsPlatelet(selection.isPlatelet);
          setSelected(selection.selected);
        }}
        allowPrintedDocument={false}
        fileUrls={fileUrls}
        uploadProcessing={isFileUploadProcessing}
        preflight={false}
        documentPrompt={
          <OptionLabel>
            <AddOnLabelName>
              Please include a lab report or consultation note confirming the
              patient&apos;s alloimmunization*
            </AddOnLabelName>
          </OptionLabel>
        }
        onFileUrlsChange={setFileUrls}
        onUploadProcessingChange={setFileUploadProcessing}
      />
    </FetalAntigenFieldsWrapper>
  );
};

export const AddOnModal: React.FC<{
  addOnModalData?: AddOnModalData;
  setAddOnModalData: React.Dispatch<
    React.SetStateAction<AddOnModalData | undefined>
  >;
}> = ({ addOnModalData, setAddOnModalData }) => {
  const { deviceType } = useDeviceHook();

  const { auth, setLoggedOut } = useAuthenticationHook();
  const { setRequisitions, searchText } = useRequisitionSetter();
  const { enabled: fetalAntigenLateAdd } = useBooleanFeatureFlag(
    FeatureFlags.FetalAntigenLateAdd,
  );
  const { enabled: fetalAntigenExpansion } = useBooleanFeatureFlag(
    FeatureFlags.FetalAntigenExpansion,
  );
  const fetalAntigenLateAddEnabled =
    fetalAntigenLateAdd && fetalAntigenExpansion;
  const reportUpdatePending = Boolean(
    addOnModalData?.row.reportUpdate?.is_pending,
  );
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [selectedAddOns, setSelectedAddOns] = useState<AddOnMode[]>([]);
  const [faSelection, setFaSelection] = useState<FetalAntigenSelection>();
  const isFetalAntigen = selectedAddOns.includes("fetal_antigen");
  const rhdIncluded =
    selectedAddOns.includes("fetal_rhd") ||
    ["Final", "Corrected", "Processing", "Ordered"].includes(
      addOnModalData?.row.rhdReleaseStatus ?? "Not Ordered",
    );
  const isRequestable = (releaseStatus?: string) =>
    !reportUpdatePending && releaseStatus === "Request";
  const eligibilityByAddOnMode: Record<AddOnMode, boolean> = {
    fetal_sex: isRequestable(addOnModalData?.row.fetalReleaseStatus),
    fetal_rhd: isRequestable(addOnModalData?.row.rhdReleaseStatus),
    fetal_antigen:
      fetalAntigenLateAddEnabled &&
      isRequestable(addOnModalData?.row.faReleaseStatus),
  };
  const faSelectionValid = isFetalAntigenSelectionValid(
    faSelection?.antigens ?? [],
    faSelection?.isRbc ?? false,
    faSelection?.isPlatelet ?? false,
  );
  const faDocumentSatisfied = hasSuccessfulFetalAntigenUpload(
    faSelection?.fileUrls ?? {},
  );
  const canSubmit =
    selectedAddOns.length > 0 &&
    (!isFetalAntigen || (faSelectionValid && faDocumentSatisfied));

  const [reportData, setReportData] = useState<DetailedRequisitionSerializer>();

  useEffect(() => {
    const getFullReportInfo = async () => {
      if (addOnModalData) {
        setReportData(undefined);
        setSubmitError("");
        setSelectedAddOns(
          addOnModalData.type !== "fetal_antigen" &&
            eligibilityByAddOnMode[addOnModalData.type]
            ? [addOnModalData.type]
            : [],
        );
        setFaSelection(undefined);
      }
      if (auth && addOnModalData?.row.sfID) {
        const reportResponse = await GetSpecificRequisition(
          auth.token,
          addOnModalData?.row.sfID,
          setLoggedOut,
        );
        if (reportResponse?.ok) {
          const report =
            (await reportResponse.json()) as DetailedRequisitionSerializer;
          setReportData(report);
        }
      }
      if (!addOnModalData) {
        setAddOnModalData(undefined);
        setReportData(undefined);
        setLoading(false);
        setSelectedAddOns([]);
        setFaSelection(undefined);
      }
    };

    getFullReportInfo();
  }, [addOnModalData]);

  useEffect(() => {
    if (addOnModalData && auth) {
      TrackAnalyticsEvent(AnalyticsEventNames.AddonModalViewed, auth.token);
    }
  }, [addOnModalData, auth]);

  return (
    <CenteredModal
      open={Boolean(addOnModalData)}
      onClose={() => {
        setAddOnModalData(undefined);
      }}
    >
      <AddOnModalWrapper deviceType={deviceType}>
        <AddOnModalTitle>
          Add-ons Selection
          <CloseButton onClick={() => setAddOnModalData(undefined)}>
            <Close />
          </CloseButton>
        </AddOnModalTitle>
        {reportData ? (
          <>
            <ModalBody>
              {reportUpdatePending && (
                <AddOnModalNotice $isError={false}>
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    color={themePalette.blueDark}
                    fontSize={16}
                    aria-hidden
                  />
                  A report update is in review. You can add tests once it&apos;s
                  complete.
                </AddOnModalNotice>
              )}
              {submitError && (
                <AddOnModalNotice $isError>
                  <WarningIcon testId="WarningIcon" />
                  {submitError}
                </AddOnModalNotice>
              )}
              <ConfirmMessage>
                Please select and confirm the additions you want to make to:
              </ConfirmMessage>

              <PatientBox>
                <PatientColumn>
                  <PatientLabel>Patient Name</PatientLabel>
                  <PatientValue>{`${addOnModalData?.row.name}`}</PatientValue>
                </PatientColumn>
                <PatientColumn>
                  <PatientLabel>Patient Date of Birth</PatientLabel>
                  <PatientValue>{`${addOnModalData?.row.dob}`}</PatientValue>
                </PatientColumn>
              </PatientBox>
              <AddOnOptions>
                {NON_ANTIGEN_ADD_ON_MODES.map((addOnMode) => {
                  const selected = selectedAddOns.includes(addOnMode);
                  const disabled =
                    !eligibilityByAddOnMode[addOnMode] ||
                    (addOnMode === "fetal_rhd" &&
                      Boolean(faSelection?.hasRhdIncompatibleAntigen));
                  return (
                    <ConfirmCheckboxLabel
                      key={addOnMode}
                      disabled={disabled}
                      control={
                        <Checkbox
                          checked={selected}
                          disabled={disabled}
                          name={addOnMode}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setSelectedAddOns((current) =>
                              checked
                                ? [...current, addOnMode]
                                : current.filter((mode) => mode !== addOnMode),
                            );
                          }}
                        />
                      }
                      label={
                        <AddOnLabelText>
                          Add{" "}
                          <AddOnLabelName>
                            {ADD_ON_LABELS[addOnMode]}
                          </AddOnLabelName>
                        </AddOnLabelText>
                      }
                    />
                  );
                })}
                {fetalAntigenLateAddEnabled && (
                  <AddOnFetalAntigenFields
                    disabled={!eligibilityByAddOnMode.fetal_antigen}
                    rhdIncluded={rhdIncluded}
                    onChange={(selection) => {
                      setFaSelection(selection);
                      setSelectedAddOns((current) => {
                        const withoutFetalAntigen = current.filter(
                          (mode) => mode !== "fetal_antigen",
                        );
                        return selection.isRbc || selection.isPlatelet
                          ? [...withoutFetalAntigen, "fetal_antigen"]
                          : withoutFetalAntigen;
                      });
                    }}
                  />
                )}
              </AddOnOptions>
              <Disclaimer>
                By clicking <strong>“Add Selection”</strong>, I acknowledge the
                tests are requested by ordering provider:{" "}
                <strong>{`${reportData?.physician_name} (NPI:${reportData?.npi})`}</strong>
              </Disclaimer>
            </ModalBody>

            <ModalDivider />

            <AddOnModalButtons>
              {!loading && (
                <Button
                  label="CANCEL"
                  onClick={() => setAddOnModalData(undefined)}
                  size="medium"
                />
              )}
              <Button
                label="ADD SELECTION"
                loading={loading}
                onClick={async () => {
                  if (addOnModalData && selectedAddOns.length > 0) {
                    setLoading(true);
                    const fetalAntigenFileUrl = isFetalAntigen
                      ? Object.values(faSelection?.fileUrls ?? {}).find(
                          ({ fileName, valid }) => valid && Boolean(fileName),
                        )?.fileName
                      : undefined;
                    const fetalAntigenPanel = isFetalAntigen
                      ? faSelection?.antigens
                      : undefined;

                    const response = await SubmitAddOns(
                      auth!.token,
                      addOnModalData?.row.reqID,
                      selectedAddOns,
                      setLoggedOut,
                      fetalAntigenPanel,
                      fetalAntigenFileUrl,
                    );

                    setLoading(false);
                    if (response?.ok) {
                      setAddOnModalData(undefined);
                      await RefreshRequisitionRows(
                        auth!.token,
                        setRequisitions,
                        setLoggedOut,
                        searchText,
                      );
                    } else {
                      const body = await response?.json().catch(() => ({}));
                      setSubmitError(
                        body?.error ||
                          "We couldn't add the tests. Please try again or contact customer service.",
                      );
                    }
                  }
                }}
                size="medium"
                className="add-on"
                disabled={!canSubmit || loading}
              />
            </AddOnModalButtons>
          </>
        ) : (
          <>
            <ModalLoadingBody aria-label="Loading add-on details">
              <ModalLoadingText variant="rectangular" animation="wave" />
              <PatientBox>
                <ModalLoadingPatientColumn>
                  <ModalLoadingPatientText
                    variant="rectangular"
                    animation="wave"
                  />
                  <ModalLoadingPatientText
                    variant="rectangular"
                    animation="wave"
                  />
                </ModalLoadingPatientColumn>
                <ModalLoadingPatientColumn>
                  <ModalLoadingPatientText
                    variant="rectangular"
                    animation="wave"
                  />
                  <ModalLoadingPatientText
                    variant="rectangular"
                    animation="wave"
                  />
                </ModalLoadingPatientColumn>
              </PatientBox>
              <ModalLoadingOption variant="rectangular" animation="wave" />
              <ModalLoadingOption variant="rectangular" animation="wave" />
              <ModalLoadingText variant="rectangular" animation="wave" />
            </ModalLoadingBody>
            <ModalDivider />
            <AddOnModalButtons>
              <Button
                label="CANCEL"
                onClick={() => setAddOnModalData(undefined)}
                size="medium"
              />
              <Button
                label="ADD SELECTION"
                onClick={() => undefined}
                size="medium"
                className="add-on"
                disabled
              />
            </AddOnModalButtons>
          </>
        )}
      </AddOnModalWrapper>
    </CenteredModal>
  );
};
